import { useEffect, useMemo } from "react";

import { map, orderBy, uniq } from "lodash/fp";
import { shallow } from "zustand/shallow";

import usePulseStore from "@app/analytics/Pulse/stores/pulse";
import type { PulseMetadata } from "@app/analytics/Pulse/types";
import {
  getDropdownOptionsByDate,
  resultForEntityAndTimePeriod,
} from "@app/analytics/Pulse/utils";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import usePulseByIdQuery from "./usePulseByIdQuery";
import usePulseMetadataQuery from "./usePulseMetadataQuery";

const EMPTY_METADATA_RESULTS: PulseMetadata[] = [];

const useForYourTeams = () => {
  const { workspace, workspaceLoading } = useWorkspace();
  const teams = workspace.teams;

  const { selectedRange, selectedTeam, initializeTeamsResults } = usePulseStore(
    (state) => ({
      ...state.forYourTeams,
      initializeTeamsResults: state.initializeTeamsResults,
    }),
    shallow
  );

  const pulseMetadataQuery = usePulseMetadataQuery();

  const metadata = pulseMetadataQuery.data?.teams ?? EMPTY_METADATA_RESULTS;

  const filteredMetadata = useMemo(() => {
    const teamIds = map("id", teams);
    return metadata.filter((d) => d.teamId && teamIds.includes(d.teamId));
  }, [metadata, teams]);

  const pulseId = resultForEntityAndTimePeriod(
    selectedTeam,
    selectedRange.value,
    metadata,
    "teamId"
  )?.id;

  const teamResultQuery = usePulseByIdQuery({
    id: pulseId,
    enabled: Boolean(selectedTeam) && Boolean(selectedRange.value),
  });

  const isLoading =
    pulseMetadataQuery.isLoading ||
    workspaceLoading ||
    teamResultQuery.isLoading;

  useEffect(() => {
    if (isLoading) return;

    const dropdownOptions = getDropdownOptionsByDate(filteredMetadata);
    const teamIdsWithResults = uniq(map("teamId", filteredMetadata));

    const resultTeams = orderBy(
      ({ id }) => teamIdsWithResults.indexOf(id),
      "asc",
      teams.filter((t) => teamIdsWithResults.includes(t.id))
    );

    initializeTeamsResults(resultTeams, dropdownOptions);
  }, [isLoading, filteredMetadata, teams]);

  const hasPermissionError =
    !teamResultQuery.isLoading &&
    teamResultQuery.isError &&
    teamResultQuery.error.message.includes("403");

  return {
    isLoading: teamResultQuery.isLoading || pulseMetadataQuery.isLoading,
    teamResultQuery,
    metadata,
    hasPermissionError,
  };
};

export default useForYourTeams;
